import ModelsSection from "./ModelsSection";
import NotificationsSection from "./NotificationsSection";
import BillingSection from "./billing/BillingSection";
import UsageSection from "./billing/UsageSection";
import { useState } from "react";
import { useTheme } from "next-themes";
import {
  CreditCard, ShieldCheck, Check, Sun, Moon, Monitor,
  Dices, Mail, LogOut, MessagesSquare, ArrowUpRight, Download,
  KeyRound, ScrollText, Plus, Copy, AlertTriangle, ChevronDown,
  MessageSquare, FileText, Ban, UserPlus, Globe, CalendarClock, ListChecks,
  XCircle, Box, Plug, Lock, OctagonPause,
} from "lucide-react";
import { toast } from "sonner";
import { SearchInput } from "@/shared/components/ui-kit";
import PixelAvatar from "@/shared/components/PixelAvatar";
import InitialsAvatar from "@/shared/components/InitialsAvatar";
import AvatarCustomizer from "@/shared/avatar/AvatarCustomizer";
import { setUserAvatar, useUserAvatar } from "@/shared/avatar/userAvatar";
import SectionNav from "@/shared/components/SectionNav";
import { MARKETPLACE_URL } from "@/shared/lib/client-app";
import { formOf, profilePatch, useSettings, useUpdateProfile } from "./hooks/useProfile";
import { ROLES, API_SCOPES, AUTONOMY_LEVELS, PERMISSION_CATEGORIES, DEFAULT_GUARDRAILS } from "./options";
import {
  memberName, useInviteMember, useMembers, useRemoveMember, useRevokeInvite, useSetMemberRole,
} from "@/shared/api/members";
import { Switch } from "@/shared/components/ui/switch";
import { Slider } from "@/shared/components/ui/slider";
import {
  AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger, AlertDialogCancel,
} from "@/shared/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/shared/components/ui/dialog";
import {
  SlidersHorizontalIcon, BellIcon, LockIcon, UsersIcon, ShieldCheckIcon,
  KeyRoundIcon, CreditCardIcon, ActivityIcon, ScrollTextIcon,
} from "@/shared/components/icons/animated";

import type { ProfileForm } from "./hooks/useProfile";
import type {
  ApiKey, ApiScopeId, AuditEvent, AutonomyLevelId, MemberRow, RoleId, User,
} from "@/shared/types";
import type { SectionNavGroup } from "@/shared/components/SectionNav";

/* -------------------------------- atoms --------------------------------- */

/** The sections the page can show: the keys of the `Body` lookup, and
    what the nav rail and the `section` prop address a section by. */
export type SettingsSectionId =
  | "general" | "notifications" | "security"
  | "members" | "permissions" | "models" | "api-keys" | "billing" | "usage" | "audit";

// Sidebar nav, grouped ChatGPT-style. Labels stay short so nothing wraps.
const NAV_GROUPS: SectionNavGroup<SettingsSectionId>[] = [
  {
    label: "Account",
    items: [
      { id: "general", label: "General", icon: SlidersHorizontalIcon },
      { id: "notifications", label: "Notifications", icon: BellIcon },
      { id: "security", label: "Security", icon: LockIcon },
    ],
  },
  {
    label: "Workspace",
    items: [
      { id: "members", label: "Members", icon: UsersIcon },
      { id: "permissions", label: "Permissions", icon: ShieldCheckIcon },
      { id: "models", label: "Models & providers", icon: SlidersHorizontalIcon },
      { id: "api-keys", label: "API keys", icon: KeyRoundIcon },
      { id: "billing", label: "Billing", icon: CreditCardIcon },
      { id: "usage", label: "Usage", icon: ActivityIcon },
      { id: "audit", label: "Audit log", icon: ScrollTextIcon },
    ],
  },
];

// The settings-UI atoms live in shared/ so non-settings surfaces can use them.
import {
  Pane, Group, Row, RowList, Note, FieldLabel, TextInput, SelectInput,
  PrimaryButton, GhostButton, DangerButton, StatusBadge,
  confirmDangerCls, confirmDialogCls,
} from "@/shared/components/settings-atoms";

/* ------------------------------- general -------------------------------- */

// Mini UI thumbnails so theme choices read at a glance.
function ThemeSwatch({ mode }: { mode: string }) {
  // The two themes' own page, card, line and ink, drawn as a thumbnail.
  const light = { bg: "#F8F8F8", card: "#FFFFFF", line: "#EBEBEB", text: "#171717" };
  const dark = { bg: "#0E0E10", card: "#1B1B1E", line: "#29292D", text: "#F4F4F5" };
  const half = mode === "system";
  const p = mode === "dark" ? dark : light;
  return (
    <div className="relative h-[72px] rounded-[9px] overflow-hidden ring-1 ring-[var(--hairline)]" style={{ background: p.bg }}>
      {half && (
        <div className="absolute inset-y-0 right-0 w-1/2" style={{ background: dark.bg }} />
      )}
      <div className="absolute left-2.5 top-2.5 bottom-2.5 w-[26%] rounded-[5px]" style={{ background: p.card, boxShadow: `inset 0 0 0 1px ${p.line}` }} />
      <div className="absolute left-[38%] top-2.5 right-2.5 space-y-[5px]">
        <div className="h-[6px] w-[62%] rounded-full" style={{ background: half ? "#737373" : p.text, opacity: 0.85 }} />
        <div className="h-[5px] w-[86%] rounded-full" style={{ background: p.line }} />
        <div className="h-[5px] w-[74%] rounded-full" style={{ background: p.line }} />
      </div>
    </div>
  );
}

function AppearancePicker() {
  const { theme, setTheme } = useTheme();
  const options = [
    { id: "light", label: "Light", icon: Sun },
    { id: "dark", label: "Dark", icon: Moon },
    { id: "system", label: "System", icon: Monitor },
  ];
  return (
    <div className="grid grid-cols-3 gap-3 py-4">
      {options.map((o) => {
        const active = (theme || "system") === o.id;
        const Icon = o.icon;
        return (
          <button
            key={o.id}
            onClick={() => setTheme(o.id)}
            className={`press text-left rounded-[12px] p-2 ring-1 transition-shadow ${
              active
                ? "ring-[1.5px] ring-[var(--text-primary)] bg-[var(--surface)]"
                : "ring-[var(--hairline)] bg-[var(--surface)] hover:ring-[var(--hairline-focus)]"
            }`}
          >
            <ThemeSwatch mode={o.id} />
            <div className="flex items-center gap-1.5 px-1 pt-2 pb-0.5">
              <Icon className="w-3 h-3 text-[var(--text-tertiary)]" strokeWidth={1.75} />
              <span className="text-[12.5px] font-medium text-[var(--text-primary)] flex-1">{o.label}</span>
              {active && <Check className="w-3 h-3 text-[var(--text-primary)]" strokeWidth={2.25} />}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function GeneralSection() {
  const { data: settings } = useSettings();

  return (
    <Pane title="General">
      <Group title="Appearance" desc="How Definable looks on this device.">
        <RowList><AppearancePicker /></RowList>
      </Group>

      {/* Keyed on the user so a switch of account rebuilds the form from the
          new values instead of leaving the old ones in the inputs. */}
      {settings && <ProfileGroup key={settings.user.id} user={settings.user} />}

      {settings && <DangerZone email={settings.user.email} />}
    </Pane>
  );
}

/** The server's limit on `first_name` / `last_name` (CONTRACT.md). */
const NAME_MAX = 50;

/* Every IANA zone the browser knows, so the field is a pick and not a spelling
   test. Computed once — the list is ~400 entries and never changes. */
const TIMEZONES: string[] =
  typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];

function ProfileGroup({ user }: { user: User }) {
  const { seed, avatar } = useUserAvatar();
  const [avatarSeed, setAvatarSeed] = useState(seed);
  const [avatarOverride, setAvatarOverride] = useState(avatar);
  const [form, setForm] = useState(() => formOf(user));
  // Ceiling: no backend field — the picker is decoration until there is one.
  const [language, setLanguage] = useState("English (US)");
  const update = useUpdateProfile();
  const set = (k: keyof ProfileForm) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  const zoneOptions = [
    { id: "", label: "Not set" },
    // A zone the server holds but this browser doesn't list must stay selectable.
    ...(user.timezone && !TIMEZONES.includes(user.timezone) ? [{ id: user.timezone, label: user.timezone }] : []),
    ...TIMEZONES.map((z) => ({ id: z, label: z.replace(/_/g, " ") })),
  ];

  const save = () => {
    setUserAvatar({ seed: avatarSeed, avatar: avatarOverride });
    const patch = profilePatch(user, form);
    if (!Object.keys(patch).length) return toast.success("Profile saved");
    // No onError: the MutationCache in `shared/api/queryClient.ts` toasts every failed
    // mutation, and it has the request_id this callback never did. A local
    // handler here is a second toast saying less (FRONTEND_RULE_BOOK.md §3).
    update.mutate(patch, { onSuccess: () => toast.success("Profile saved") });
  };

  // The fields keep one width so the column of controls reads as a form.
  const field = "w-[300px] max-w-full";
  return (
    <Group title="Profile" desc="How you appear across your workspace.">
      <RowList
        footer={
          <PrimaryButton onClick={save} disabled={update.isPending}>
            {update.isPending ? "Saving…" : "Save changes"}
          </PrimaryButton>
        }
      >
        <div className="py-4">
          <div className="text-[13.5px] font-medium text-[var(--text-primary)]">Your character</div>
          <div className="text-[12.5px] text-[var(--text-tertiary)] mt-0.5 mb-3.5">
            Shuffle for a fresh one, or set it piece by piece. Agents draw from a separate set
            of chassis colours, so one is never mistaken for you.
          </div>
          <AvatarCustomizer
            human
            seed={avatarSeed}
            avatar={avatarOverride}
            onChange={({ seed: next, avatar: look }) => {
              setAvatarSeed(next);
              setAvatarOverride(look);
            }}
          />
        </div>
        {/* 50 is the server's ceiling (CONTRACT.md) — stopping the keystroke
            beats a round trip that comes back as a validation error. */}
        <Row title="First name">
          <div className={field}><TextInput value={form.first_name} onChange={set("first_name")} maxLength={NAME_MAX} aria-label="First name" /></div>
        </Row>
        <Row title="Last name">
          <div className={field}><TextInput value={form.last_name} onChange={set("last_name")} maxLength={NAME_MAX} aria-label="Last name" /></div>
        </Row>
        {/* Read-only: the email is the identity provider's, and changing
            it means re-verifying a login, not editing a profile. */}
        <Row title="Email" desc="Your sign-in. It comes from your login provider.">
          <div className={`${field} h-9 flex items-center px-3 rounded-[10px] bg-[var(--surface-2)] text-[13.5px] text-[var(--text-secondary)] truncate`}>
            {user.email}
          </div>
        </Row>
        <Row title="Language">
          <div className={field}><TextInput value={language} onChange={setLanguage} aria-label="Language" /></div>
        </Row>
        <Row title="Timezone" desc="Schedules and daily charts use it.">
          <SelectInput value={form.timezone} onChange={set("timezone")} options={zoneOptions} className={field} />
        </Row>
      </RowList>
    </Group>
  );
}

function DangerZone({ email }: { email: string }) {
  const [confirmText, setConfirmText] = useState("");
  return (
    <Group title="Danger zone">
      <RowList>
      <Row title="Delete account" desc="Permanently removes your agents, chats, knowledge and billing history.">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <DangerButton>Delete account</DangerButton>
          </AlertDialogTrigger>
          <AlertDialogContent className={`${confirmDialogCls} max-w-[420px]`}>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-[15px] tracking-[-0.01em]">Delete your account?</AlertDialogTitle>
              <AlertDialogDescription className="text-[13px] leading-[1.6]">
                All agents, chats and data are erased immediately. Type{" "}
                <span className="font-medium text-[var(--text-primary)]">{email}</span> to confirm.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <TextInput value={confirmText} onChange={setConfirmText} placeholder={email} />
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-[10px] text-[13px]" onClick={() => setConfirmText("")}>
                Cancel
              </AlertDialogCancel>
              <button
                disabled={confirmText !== email}
                onClick={() => toast("Account deletion is coming soon")}
                className={confirmDangerCls}
              >
                Delete forever
              </button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Row>
      </RowList>
    </Group>
  );
}

/* ------------------------------- security -------------------------------- */

function SecuritySection() {
  return (
    <Pane title="Security" desc="Keep your account — and every agent it runs — locked down.">
      <Group>
        <RowList>
          <Row title="Password">
            <GhostButton onClick={() => toast("Changing your password is coming soon")}>Change password</GhostButton>
          </Row>
          <Row
            title="Two-factor authentication"
            desc="Authenticator app · required before agents can access private browsers"
          >
            <span title="Two-factor authentication isn't available yet">
              <Switch checked={false} disabled />
            </span>
          </Row>
        </RowList>
      </Group>

      <Group
        title="Active sessions"
        action={
          <button
            onClick={() => toast("Signing out other sessions is coming soon")}
            className="press inline-flex items-center gap-1 text-[12.5px] font-medium text-[var(--danger)] hover:opacity-80"
          >
            <LogOut className="w-3 h-3" strokeWidth={1.75} /> Sign out all others
          </button>
        }
      >
        <RowList>
          <div className="py-4 text-[12.5px] text-[var(--text-quaternary)] italic">The session list isn't available yet.</div>
        </RowList>
      </Group>

      <Group title="Data">
        <RowList>
          <Row
            title="Export your data"
            desc="Agents, chats, knowledge and task history as a portable archive."
          >
            <span title="Data export isn't available yet">
              <GhostButton disabled>
                Request export <ArrowUpRight className="w-3 h-3" strokeWidth={1.75} />
              </GhostButton>
            </span>
          </Row>
        </RowList>
      </Group>
    </Pane>
  );
}

/* --------------------------- members & roles ------------------------------ */

/** "Mar 2025" from an ISO date. */
const monthYear = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: "short", year: "numeric" });

function MembersSection() {
  const { data: settings } = useSettings();
  const { data: members, isPending, isError } = useMembers();
  const invite = useInviteMember();
  const setRoleApi = useSetMemberRole();
  const revokeApi = useRevokeInvite();
  const removeApi = useRemoveMember();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"admin" | "member">("member");

  const me = settings?.user.id;
  const rows = members ?? [];
  const assignableRoles = ROLES.filter((r) => r.id !== "owner");
  const roleLabel = (role: RoleId) => ROLES.find((r) => r.id === role)?.label ?? role;

  const setRole = (m: MemberRow, role: RoleId) =>
    setRoleApi.mutate(
      { userId: m.user_id, role },
      { onSuccess: () => toast.success(`${memberName(m)} is now ${role === "admin" ? "an Admin" : "a Member"}`) },
    );

  const sendInvite = () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Enter a valid email address");
      return;
    }
    if (rows.some((m) => m.email === email)) {
      toast.error("Already in this workspace");
      return;
    }
    invite.mutate(
      { email, role: inviteRole },
      {
        onSuccess: () => {
          setInviteOpen(false);
          setInviteEmail("");
          setInviteRole("member");
          toast.success(`Invite sent to ${email}`);
        },
      },
    );
  };

  const revokeInvite = (m: MemberRow) =>
    revokeApi.mutate(m.user_id, { onSuccess: () => toast(`Invite to ${m.email} revoked`) });

  const removeMember = (m: MemberRow) =>
    removeApi.mutate(m.user_id, { onSuccess: () => toast(`${memberName(m)} removed from the workspace`) });

  return (
    <Pane
      title="Members"
      desc="Everyone here shares your agents, credits and connected apps."
      action={
        <PrimaryButton onClick={() => setInviteOpen(true)}>
          <UserPlus className="w-3.5 h-3.5" strokeWidth={1.75} /> Invite member
        </PrimaryButton>
      }
    >
      <Group>
        {isPending ? (
          <div className="py-3 text-[12.5px] text-[var(--text-quaternary)] italic">Loading members…</div>
        ) : isError ? (
          <div className="py-3 text-[12.5px] text-[var(--text-quaternary)] italic">Couldn't load members.</div>
        ) : (
          <RowList
            header={
              <>
                <span className="flex-1">
                  Member <span className="text-[var(--text-quaternary)]">· <span className="tabular-nums">{rows.length}</span></span>
                </span>
                <span className="w-[92px]">Role</span>
                <span className="w-[46px]" />
              </>
            }
          >
            {rows.map((m) => {
              const invited = m.status === "invited";
              const you = m.user_id === me;
              const name = memberName(m);
              return (
                <div key={m.user_id} className="flex items-center gap-3 py-3">
                  <InitialsAvatar name={name} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span title={name} className="text-[13.5px] font-medium text-[var(--text-primary)] truncate">{name}</span>
                      {you && <StatusBadge>You</StatusBadge>}
                      {invited && <StatusBadge tone="warn">Invited</StatusBadge>}
                    </div>
                    <div className="text-[12px] text-[var(--text-tertiary)] mt-[1px] truncate">
                      {name !== m.email && `${m.email} · `}
                      {invited || !m.joined_at ? "Invite pending" : `Joined ${monthYear(m.joined_at)}`}
                    </div>
                  </div>
                  {/* Nobody changes their own role, and an owner is changed by transfer. */}
                  {m.role === "owner" || you ? (
                    <span className="w-[92px] inline-flex items-center gap-1.5 h-7 text-[12.5px] text-[var(--text-tertiary)]">
                      {m.role === "owner" && <Lock className="w-3 h-3 text-[var(--text-tertiary)]" strokeWidth={1.75} />}
                      {roleLabel(m.role)}
                    </span>
                  ) : (
                    <SelectInput
                      compact
                      className="w-[92px]"
                      value={m.role}
                      onChange={(r) => setRole(m, r as RoleId)}
                      options={assignableRoles}
                    />
                  )}
                  {invited ? (
                    <button
                      onClick={() => revokeInvite(m)}
                      className="press shrink-0 text-[12.5px] font-medium text-[var(--danger)] hover:opacity-80"
                    >
                      Revoke
                    </button>
                  ) : m.role !== "owner" && !you ? (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button className="press shrink-0 text-[12.5px] font-medium text-[var(--text-tertiary)] hover:text-[var(--danger)]">
                          Remove
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className={`${confirmDialogCls} max-w-[400px]`}>
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-[15px] tracking-[-0.01em]">Remove {name}?</AlertDialogTitle>
                          <AlertDialogDescription className="text-[13px] leading-[1.6]">
                            They lose access to every agent, chat and knowledge in this workspace.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-[10px] text-[13px]">Cancel</AlertDialogCancel>
                          <button onClick={() => removeMember(m)} className={confirmDangerCls}>
                            Remove member
                          </button>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  ) : (
                    <span className="w-[46px]" />
                  )}
                </div>
              );
            })}
          </RowList>
        )}
        <Note>Admins can manage agents, apps and members. Members can run agents but can't change workspace settings. Invites expire after 7 days.</Note>
      </Group>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className={`${confirmDialogCls} w-[400px] max-w-[92vw] p-6 gap-0`}>
          <DialogTitle className="text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">
            Invite a member
          </DialogTitle>
          <DialogDescription className="text-[13px] leading-[1.55] text-[var(--text-tertiary)] mt-1">
            They'll get an email invite and join when they accept it.
          </DialogDescription>
          <div className="mt-5 space-y-4">
            <div>
              <FieldLabel>Email</FieldLabel>
              <TextInput
                value={inviteEmail}
                onChange={setInviteEmail}
                type="email"
                placeholder="teammate@studio.co"
              />
            </div>
            <div>
              <FieldLabel>Role</FieldLabel>
              <SelectInput value={inviteRole} onChange={(r) => setInviteRole(r as "admin" | "member")} options={assignableRoles} />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <GhostButton onClick={() => setInviteOpen(false)}>Cancel</GhostButton>
            <PrimaryButton onClick={sendInvite} disabled={invite.isPending}>Send invite</PrimaryButton>
          </div>
        </DialogContent>
      </Dialog>
    </Pane>
  );
}

/* ------------------------ permissions & autonomy -------------------------- */

function AutonomyControl({ value, onChange }: {
  value: AutonomyLevelId;
  onChange: (id: AutonomyLevelId) => void;
}) {
  return (
    <div className="inline-flex shrink-0 p-[3px] rounded-full bg-[var(--surface-2)]">
      {AUTONOMY_LEVELS.map((l) => {
        const active = l.id === value;
        return (
          <button
            key={l.id}
            onClick={() => onChange(l.id)}
            className={`press px-2.5 py-[4px] rounded-full text-[12px] font-medium whitespace-nowrap ${
              active
                ? "bg-[var(--surface)] text-[var(--text-primary)] ring-1 ring-[var(--hairline)] shadow-lift"
                : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
            }`}
          >
            {l.label}
          </button>
        );
      })}
    </div>
  );
}

function PermissionsSection() {
  const [levels, setLevels] = useState<Record<string, AutonomyLevelId>>(
    Object.fromEntries(PERMISSION_CATEGORIES.map((c) => [c.id, c.level] as const))
  );
  const [creditCap, setCreditCap] = useState(DEFAULT_GUARDRAILS.monthlyCreditCap);
  const [tokenCap, setTokenCap] = useState(DEFAULT_GUARDRAILS.perRunTokenCap);
  const [paused, setPaused] = useState(false);

  return (
    <Pane
      title="Permissions & autonomy"
      desc="What agents may do on their own, and when they must stop and ask you first."
    >
      <Group>
        <RowList>
          {PERMISSION_CATEGORIES.map((c) => (
            <Row key={c.id} title={c.label} desc={c.desc}>
              <AutonomyControl
                value={levels[c.id]}
                onChange={(v) => setLevels((p) => ({ ...p, [c.id]: v }))}
              />
            </Row>
          ))}
        </RowList>
        <Note>"Ask above limits" defers to the spend caps below — anything within them runs without a prompt.</Note>
      </Group>

      <Group title="Spend caps" desc="Hard limits agents can never cross, no matter how autonomous.">
        <RowList>
          <div className="py-4">
            <div className="flex items-baseline justify-between mb-3">
              <div>
                <div className="text-[13.5px] font-medium text-[var(--text-primary)]">Monthly credit cap</div>
                <div className="text-[12.5px] text-[var(--text-tertiary)] mt-[1px]">
                  Agents pause when combined spend hits this ceiling.
                </div>
              </div>
              <span className="font-mono text-[13px] tabular-nums text-[var(--text-primary)]">
                {creditCap.toLocaleString()} cr
              </span>
            </div>
            <Slider value={[creditCap]} onValueChange={([v]) => setCreditCap(v)} min={1000} max={20000} step={500} />
            <div className="flex justify-between mt-1.5 font-mono text-[11px] text-[var(--text-quaternary)]">
              <span>1,000</span>
              <span>20,000</span>
            </div>
          </div>
          <div className="py-4">
            <div className="flex items-baseline justify-between mb-3">
              <div>
                <div className="text-[13.5px] font-medium text-[var(--text-primary)]">Per-run token cap</div>
                <div className="text-[12.5px] text-[var(--text-tertiary)] mt-[1px]">
                  A single run stops for approval past this many tokens.
                </div>
              </div>
              <span className="font-mono text-[13px] tabular-nums text-[var(--text-primary)]">
                {Math.round(tokenCap / 1000)}k tok
              </span>
            </div>
            <Slider value={[tokenCap]} onValueChange={([v]) => setTokenCap(v)} min={10000} max={500000} step={10000} />
            <div className="flex justify-between mt-1.5 font-mono text-[11px] text-[var(--text-quaternary)]">
              <span>10k</span>
              <span>500k</span>
            </div>
          </div>
        </RowList>
      </Group>

      <Group title="Kill switch">
        <RowList>
        <Row
          title={
            <span className="inline-flex items-center gap-2">
              Pause all agents
              {paused && <StatusBadge tone="danger">Paused</StatusBadge>}
            </span>
          }
          desc="Instantly halts every running agent and queued task. Nothing moves until you resume."
        >
          {paused ? (
            <GhostButton
              onClick={() => {
                setPaused(false);
                toast.success("Agents resumed — schedules pick up on their next trigger");
              }}
            >
              Resume agents
            </GhostButton>
          ) : (
            <AlertDialog>
              {/* Nothing can pause runs yet, so the trigger says so instead of opening. */}
              <span title="Pausing agents isn't available yet">
                <DangerButton className="opacity-40 pointer-events-none">
                  <OctagonPause className="w-3.5 h-3.5" strokeWidth={1.75} /> Pause all agents
                </DangerButton>
              </span>
              <AlertDialogContent className={`${confirmDialogCls} max-w-[420px]`}>
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-[15px] tracking-[-0.01em]">Pause all agents?</AlertDialogTitle>
                  <AlertDialogDescription className="text-[13px] leading-[1.6]">
                    Every agent stops mid-run and every schedule is suspended. In-flight approvals are cancelled. You can resume at any time.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-[10px] text-[13px]">Cancel</AlertDialogCancel>
                  <button
                    onClick={() => {
                      setPaused(true);
                      toast("All agents paused", {
                        description: "Runs halted and schedules suspended.",
                      });
                    }}
                    className={confirmDangerCls}
                  >
                    Pause everything
                  </button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </Row>
        </RowList>
      </Group>
    </Pane>
  );
}

/* -------------------------------- API keys -------------------------------- */

function ApiKeysSection() {
  // No API-key endpoints yet: the list starts empty and Create is closed.
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [step, setStep] = useState("form"); // "form" | "reveal"
  const [newName, setNewName] = useState("");
  const [newScopes, setNewScopes] = useState<ApiScopeId[]>(["agents:read"]);
  const [fullKey, setFullKey] = useState("");

  const openCreate = () => {
    setStep("form");
    setNewName("");
    setNewScopes(["agents:read"]);
    setFullKey("");
    setCreateOpen(true);
  };

  const toggleScope = (id: ApiScopeId) =>
    setNewScopes((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const createKey = () => {
    if (!newName.trim()) {
      toast.error("Give the key a name");
      return;
    }
    if (newScopes.length === 0) {
      toast.error("Select at least one scope");
      return;
    }
    const secret = `df_live_${(Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)).slice(0, 24)}`;
    setFullKey(secret);
    setKeys((ks) => [
      {
        id: `k-${Date.now()}`,
        name: newName.trim(),
        masked: `df_live_••••${secret.slice(-4)}`,
        created: "Jul 23, 2026",
        lastUsed: "Never",
        scopes: [...newScopes],
      },
      ...ks,
    ]);
    setStep("reveal");
  };

  const copyKey = () => {
    navigator.clipboard?.writeText(fullKey);
    toast.success("Key copied to clipboard");
  };

  const revoke = (id: string) => {
    const k = keys.find((x) => x.id === id)!;
    setKeys((ks) => ks.filter((x) => x.id !== id));
    toast(`Key "${k.name}" revoked`, { description: "Requests using it fail immediately." });
  };

  return (
    <Pane
      title="API keys"
      desc="Drive Definable programmatically — trigger agents, manage tasks and read results."
      action={
        <span title="API keys aren't available yet">
          <PrimaryButton onClick={openCreate} disabled>
            <Plus className="w-3.5 h-3.5" strokeWidth={2} /> Create key
          </PrimaryButton>
        </span>
      }
    >
      <Group>
        <RowList
          header={
            <>
              <span className="flex-1">Name</span>
              <span className="w-[160px] text-right">Last used</span>
              <span className="w-[46px]" />
            </>
          }
        >
          {keys.length === 0 && (
            <div className="py-10 flex flex-col items-center gap-2 text-center">
              <div className="w-10 h-10 rounded-[12px] bg-[var(--surface-2)] flex items-center justify-center">
                <KeyRound className="w-[18px] h-[18px] text-[var(--text-tertiary)]" strokeWidth={1.5} />
              </div>
              <div className="mt-1 text-[14px] font-medium text-[var(--text-primary)]">No API keys yet</div>
              <div className="text-[12.5px] text-[var(--text-tertiary)] max-w-[320px]">
                Keys for your own code show up here. Provider keys live in Models &amp; providers.
              </div>
            </div>
          )}
          {keys.map((k) => (
            <div key={k.id} className="flex items-center gap-4 py-3.5">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[13.5px] font-medium text-[var(--text-primary)] truncate">{k.name}</span>
                  <code className="shrink-0 font-mono text-[11.5px] px-1.5 py-[2px] rounded-[6px] bg-[var(--surface-2)] text-[var(--text-secondary)]">
                    {k.masked}
                  </code>
                </div>
                <div className="font-mono text-[11px] text-[var(--text-quaternary)] mt-[3px] truncate">
                  {k.scopes.join(" · ")}
                </div>
              </div>
              <div className="w-[160px] shrink-0 text-right">
                <div className="text-[12px] text-[var(--text-tertiary)]">Last used {k.lastUsed}</div>
                <div className="text-[11.5px] text-[var(--text-quaternary)] mt-[1px]">Created {k.created}</div>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button className="press shrink-0 text-[12.5px] font-medium text-[var(--text-tertiary)] hover:text-[var(--danger)]">
                    Revoke
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent className={`${confirmDialogCls} max-w-[400px]`}>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-[15px] tracking-[-0.01em]">Revoke "{k.name}"?</AlertDialogTitle>
                    <AlertDialogDescription className="text-[13px] leading-[1.6]">
                      Any integration using <span className="font-mono text-[12.5px]">{k.masked}</span> stops working immediately. This cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-[10px] text-[13px]">Cancel</AlertDialogCancel>
                    <button onClick={() => revoke(k.id)} className={confirmDangerCls}>
                      Revoke key
                    </button>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ))}
        </RowList>
        <Note>
          Keys act with your workspace permissions. See the{" "}
          <a
            href="#api-docs"
            onClick={(e) => e.preventDefault()}
            className="font-medium text-[var(--text-secondary)] underline underline-offset-2 decoration-[var(--hairline-focus)] hover:text-[var(--text-primary)]"
          >
            API docs
          </a>{" "}
          for authentication and rate limits.
        </Note>
      </Group>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className={`${confirmDialogCls} w-[440px] max-w-[92vw] p-6 gap-0`}>
          {step === "form" ? (
            <>
              <DialogTitle className="text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">
                Create API key
              </DialogTitle>
              <DialogDescription className="text-[13px] leading-[1.55] text-[var(--text-tertiary)] mt-1">
                Scope it to the minimum your integration needs.
              </DialogDescription>
              <div className="mt-5 space-y-4">
                <div>
                  <FieldLabel>Key name</FieldLabel>
                  <TextInput value={newName} onChange={setNewName} placeholder="e.g. Production backend" />
                </div>
                <div>
                  <FieldLabel>Scopes</FieldLabel>
                  <div className="rounded-[10px] ring-1 ring-[var(--hairline-strong)] overflow-hidden">
                    {API_SCOPES.map((s, i) => {
                      const on = newScopes.includes(s.id);
                      return (
                        <button
                          key={s.id}
                          onClick={() => toggleScope(s.id)}
                          className={`press w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-[var(--accent-hover)] ${i > 0 ? "border-t hairline" : ""}`}
                        >
                          <span
                            className={`w-4 h-4 shrink-0 rounded-[4.5px] flex items-center justify-center ${
                              on ? "bg-[var(--brand)]" : "ring-1 ring-[var(--hairline-focus)]"
                            }`}
                          >
                            {on && <Check className="w-2.5 h-2.5 text-[var(--brand-foreground)]" strokeWidth={3} />}
                          </span>
                          <span className="flex-1 text-[13px] text-[var(--text-primary)]">{s.label}</span>
                          <span className="font-mono text-[11px] text-[var(--text-quaternary)]">{s.id}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <GhostButton onClick={() => setCreateOpen(false)}>Cancel</GhostButton>
                <PrimaryButton onClick={createKey}>Create key</PrimaryButton>
              </div>
            </>
          ) : (
            <>
              <DialogTitle className="text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">
                "{newName.trim()}" created
              </DialogTitle>
              <DialogDescription className="text-[13px] leading-[1.55] text-[var(--text-tertiary)] mt-1">
                Copy your key now — it's only shown this once.
              </DialogDescription>
              <div className="mt-5 rounded-[10px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] p-3 flex items-center gap-2.5">
                <code className="flex-1 font-mono text-[12.5px] break-all text-[var(--text-primary)]">{fullKey}</code>
                <button
                  onClick={copyKey}
                  className="press shrink-0 p-2 rounded-full ring-1 ring-[var(--hairline-strong)] bg-[var(--surface)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                  title="Copy to clipboard"
                >
                  <Copy className="w-3.5 h-3.5" strokeWidth={1.75} />
                </button>
              </div>
              <div className="mt-3 flex items-start gap-2 rounded-[10px] bg-[var(--warn-bg)] ring-1 ring-[var(--warn-ring)] px-3 py-2.5">
                <AlertTriangle className="w-3.5 h-3.5 mt-[1px] shrink-0 text-[var(--warn-text)]" strokeWidth={1.75} />
                <span className="text-[12.5px] leading-[1.5] text-[var(--warn-text)]">
                  You won't see this again. Store it in a secret manager — anyone with the key can act as your workspace.
                </span>
              </div>
              <div className="flex justify-end mt-6">
                <PrimaryButton onClick={() => setCreateOpen(false)}>Done</PrimaryButton>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Pane>
  );
}

/* -------------------------------- audit log ------------------------------- */

const AUDIT_ICONS = {
  mail: Mail,
  sandbox: Box,
  message: MessageSquare,
  app: Plug,
  task: ListChecks,
  fail: XCircle,
  block: Ban,
  key: KeyRound,
  invite: UserPlus,
  file: FileText,
  browser: Globe,
  credits: CreditCard,
  schedule: CalendarClock,
  shield: ShieldCheck,
  plan: ArrowUpRight,
  session: Monitor,
};

const AUDIT_FILTERS = [
  { id: "all", label: "All" },
  { id: "agents", label: "Agents" },
  { id: "security", label: "Security" },
  { id: "billing", label: "Billing" },
];

// No audit endpoint yet: the trail is empty rather than invented.
const AUDIT_EVENTS: AuditEvent[] = [];

function AuditSection() {
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase();
  const filtered = AUDIT_EVENTS.filter(
    (e) =>
      (filter === "all" || e.category === filter) &&
      (q === "" ||
        [e.action, e.actor, e.detail, e.id].some((f) => f.toLowerCase().includes(q)))
  );

  const exportCsv = () => {
    const esc = (v: string) => `"${String(v).replace(/"/g, '""')}"`;
    const rows = [
      ["id", "category", "actor", "action", "detail", "time"],
      ...filtered.map((e) => [e.id, e.category, e.actor, e.action, e.detail, e.time]),
    ];
    const blob = new Blob([rows.map((r) => r.map(esc).join(",")).join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "definable-audit-log.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} events to CSV`);
  };

  return (
    <Pane
      title="Audit log"
      desc="Every consequential action — by you, your team or your agents — in one trail."
      action={
        <GhostButton onClick={exportCsv}>
          <Download className="w-3.5 h-3.5" strokeWidth={1.75} /> Export CSV
        </GhostButton>
      }
    >
      <Group>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="inline-flex shrink-0 p-[3px] rounded-full bg-[var(--surface-2)] max-w-full overflow-x-auto no-scrollbar">
            {AUDIT_FILTERS.map((f) => {
              const active = filter === f.id;
              return (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={`press shrink-0 px-2.5 py-[4px] rounded-full text-[12px] font-medium whitespace-nowrap ${
                    active
                      ? "bg-[var(--surface)] text-[var(--text-primary)] ring-1 ring-[var(--hairline)] shadow-lift"
                      : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                  }`}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
          <div className="flex-1" />
          <SearchInput
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search events"
            className="w-[200px]"
          />
        </div>
        <RowList>
          {filtered.length === 0 && (
            <div className="py-10 text-center text-[13px] text-[var(--text-tertiary)]">
              {AUDIT_EVENTS.length ? "No events match your filters." : "No events yet."}
            </div>
          )}
          {filtered.map((e) => {
            const Icon = AUDIT_ICONS[e.kind] || ScrollText;
            return (
              <div key={e.id} className="flex items-center gap-3 py-3">
                <div className="w-7 h-7 shrink-0 rounded-[8px] bg-[var(--surface-2)] flex items-center justify-center">
                  <Icon
                    className={`w-3.5 h-3.5 ${e.kind === "fail" || e.kind === "block" ? "text-[var(--danger)]" : "text-[var(--text-secondary)]"}`}
                    strokeWidth={1.75}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-[var(--text-primary)] truncate">{e.action}</div>
                  <div className="text-[12px] text-[var(--text-tertiary)] mt-[1px] truncate">
                    {e.actor} · {e.detail}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[12px] text-[var(--text-tertiary)]">{e.time}</div>
                  <div className="font-mono text-[11px] text-[var(--text-quaternary)] mt-[1px]">{e.id}</div>
                </div>
              </div>
            );
          })}
        </RowList>
        {AUDIT_EVENTS.length > 0 && <Note>Showing {filtered.length} of {AUDIT_EVENTS.length} events.</Note>}
      </Group>
    </Pane>
  );
}

/* -------------------------------- dialog ---------------------------------- */

/* Workspace Settings: a page in the main card. The sections sit in a rail on the
   left of the card (the same `SectionNav` as an agent's Settings tab), the open one
   to its right. App owns which section is open, so every way in — the profile pill,
   Upgrade, "Add connection", a checkout return — lands on one. */
export default function SettingsPage({ section = "general", onSectionChange, onSignOut }: {
  section?: SettingsSectionId;
  onSectionChange: (section: SettingsSectionId) => void;
  /* Injected by the shell rather than imported: `features/auth` owns signing
     out, and one feature may not import another (rule book §1.5). */
  onSignOut?: () => void;
}) {
  const { data: settings } = useSettings();
  const Body = {
    general: GeneralSection,
    notifications: NotificationsSection,
    members: MembersSection,
    permissions: PermissionsSection,
    models: ModelsSection,
    "api-keys": ApiKeysSection,
    billing: BillingSection,
    usage: UsageSection,
    security: SecuritySection,
    audit: AuditSection,
  }[section] || GeneralSection;
  return (
    <div className="flex-1 min-w-0 min-h-0 flex flex-col md:flex-row">
      <SectionNav
        title="Settings"
        groups={NAV_GROUPS}
        active={section}
        onSelect={onSectionChange}
        footer={
          <>
            {/* the other door of the site's sign-in page: the agents other people publish */}
            <a
              href={MARKETPLACE_URL}
              className="press mb-1.5 w-full h-[30px] flex items-center gap-2 px-2 rounded-[8px] text-[13.5px] text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]"
            >
              <MessagesSquare className="w-4 h-4 shrink-0" strokeWidth={1.75} />
              <span>Use agents</span>
            </a>
            <div className="px-2 pb-1.5 text-[11.5px] leading-4 text-[var(--text-quaternary)] truncate">
              {settings?.user.email}
            </div>
            <button
              onClick={() => onSignOut?.()} // no args: signOut's optional first one is the Stytch client, not the click event
              className="press w-full h-[30px] flex items-center gap-2 px-2 rounded-[8px] text-left text-[13.5px] text-[var(--danger)] hover:bg-[var(--danger-bg)]"
            >
              <LogOut className="w-4 h-4 shrink-0" strokeWidth={1.75} />
              <span>Sign out</span>
            </button>
          </>
        }
      />
      <div className="flex-1 min-w-0 min-h-0 overflow-y-auto overflow-x-hidden">
        <div key={section} className="max-w-[880px] mx-auto px-4 md:px-10 pt-6 md:pt-10 pb-16">
          <Body />
        </div>
      </div>
    </div>
  );
}
